import { FormControl, FormHelperText, FormLabel, Stack, Typography } from '@healthinal/ui';
import { ComponentProps, ReactNode } from 'react';
import {
  Controller,
  FieldPath,
  FieldValues,
  PathValue,
  RegisterOptions,
  SetValueConfig,
  UseFormGetFieldState,
  UseFormSetValue,
  UseFormWatch,
  useFormContext,
  type Control,
} from 'react-hook-form';
import { AddressContactDto, AddressDto, AddressDtoAddressType } from '../../api/generated/model';
import { SelectAddress } from '../../routes/components/patient-address/SelectAddress.tsx';
import { SelectAddressContact } from '../../routes/components/patient-address/SelectAddressContact.tsx';
import { SingleLineFormControl } from './SingeLineFormComponents.tsx';

type FormMethods<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  getFieldState?: UseFormGetFieldState<TFieldValues>;
};

type FieldsLayout = 'row' | 'fragment' | 'singleLine';
type ContactClearMode = 'always' | 'when-empty' | 'never';

interface InstitutionContactFieldsProps<TFieldValues extends FieldValues> {
  addressField: FieldPath<TFieldValues>;
  contactField: FieldPath<TFieldValues>;
  formMethods?: FormMethods<TFieldValues>;
  layout?: FieldsLayout;
  addressLabel?: ReactNode;
  contactLabel?: ReactNode;
  addressTypes?: AddressDtoAddressType[];
  canChangeAddressType?: boolean;
  addressRules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
  contactRules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
  addressHelperText?: ReactNode;
  contactHelperText?: ReactNode;
  addressError?: boolean;
  contactError?: boolean;
  clearContactOnAddressChange?: ContactClearMode;
  contactClearOptions?: SetValueConfig | false;
  useAllContactsList?: boolean;
  isAddressDisabled?: boolean;
  isContactDisabled?: boolean;
  patientId?: string;
  addressPatientId?: string;
  contactPatientId?: string;
  contactId?: string;
  variant?: ComponentProps<typeof SelectAddress>['variant'];
  addressVariant?: ComponentProps<typeof SelectAddress>['variant'];
  contactVariant?: ComponentProps<typeof SelectAddressContact>['variant'];
  showSyncIcon?: boolean;
  showAddressSyncIcon?: boolean;
  showContactSyncIcon?: boolean;
  createOptionLabel?: string;
  addressFormControlProps?: Omit<ComponentProps<typeof FormControl>, 'error'>;
  contactFormControlProps?: Omit<ComponentProps<typeof FormControl>, 'error'>;
  onAddressChange?: (address: AddressDto | null) => void;
  onContactChange?: (contact: AddressContactDto | null, address?: AddressDto) => void;
  onAutoSelectFromUniqueMatch?: (address: AddressDto) => void;
}

export function InstitutionContactFields<TFieldValues extends FieldValues>({
  addressField,
  contactField,
  formMethods,
  layout = 'row',
  addressLabel = 'Institutionen',
  contactLabel = 'Ansprechpersonen',
  addressTypes = Object.values(AddressDtoAddressType),
  canChangeAddressType = true,
  addressRules,
  contactRules,
  addressHelperText,
  contactHelperText,
  addressError,
  contactError,
  clearContactOnAddressChange = 'when-empty',
  contactClearOptions = { shouldDirty: true },
  useAllContactsList = true,
  isAddressDisabled = false,
  isContactDisabled = false,
  patientId,
  addressPatientId,
  contactPatientId,
  contactId,
  variant,
  addressVariant = variant,
  contactVariant = variant,
  showSyncIcon = false,
  showAddressSyncIcon = showSyncIcon,
  showContactSyncIcon = showSyncIcon,
  createOptionLabel,
  addressFormControlProps,
  contactFormControlProps,
  onAddressChange,
  onContactChange,
  onAutoSelectFromUniqueMatch,
}: InstitutionContactFieldsProps<TFieldValues>) {
  const contextMethods = useFormContext<TFieldValues>() as FormMethods<TFieldValues> | null;
  const methods = formMethods ?? contextMethods;

  if (!methods) {
    throw new Error('InstitutionContactFields must be used with formMethods or inside a FormProvider');
  }

  const { control, watch, setValue, getFieldState } = methods;

  const addressValue = watch(addressField) as AddressDto | null;
  const contactValue = watch(contactField) as AddressContactDto | null;
  const addressFieldState = getFieldState?.(addressField);
  const contactFieldState = getFieldState?.(contactField);
  const hasAddressError = addressError ?? !!addressFieldState?.error;
  const hasContactError = contactError ?? !!contactFieldState?.error;
  const addressSelectVariant = addressVariant;
  const contactSelectVariant = contactVariant;
  const addressSelectPatientId = addressPatientId ?? patientId;
  const contactSelectPatientId = contactPatientId ?? patientId;
  const addressSelectContactId = contactId ?? contactValue?.id ?? undefined;

  const setContactValue = (value: AddressContactDto | null) => {
    if (contactClearOptions === false) {
      setValue(contactField, value as PathValue<TFieldValues, typeof contactField>);
      return;
    }

    setValue(contactField, value as PathValue<TFieldValues, typeof contactField>, contactClearOptions);
  };

  /**
   * Writes back the contact a sync returned — including null, when the person is no longer at the
   * Institution. Deliberately not `setContactValue`: that applies `contactClearOptions`, which
   * marks the field dirty and would have the autosave PUT the value straight back to the server it
   * just came from. This is server truth, not a user edit.
   */
  const applySyncedContact = (contact: AddressContactDto | null) => {
    setValue(contactField, contact as PathValue<TFieldValues, typeof contactField>, {
      shouldDirty: false,
    });
  };

  const shouldClearContact = (address: AddressDto | null) => {
    if (clearContactOnAddressChange === 'never') {
      return false;
    }

    if (contactValue) {
      return false;
    }

    return clearContactOnAddressChange === 'always' || !address;
  };

  const isSameAddress = (first: AddressDto, second: AddressDto) => {
    if (first.id && second.id) {
      return first.id === second.id;
    }

    return (
      first.address === second.address &&
      first.organization === second.organization &&
      first.city === second.city &&
      first.addressType === second.addressType &&
      first.phone === second.phone
    );
  };

  const renderLabel = (label: ReactNode) => (typeof label === 'string' ? <Typography>{label}</Typography> : label);

  const addressControl = (
    <Controller
      name={addressField}
      control={control}
      rules={addressRules}
      render={({ field: { onChange, value } }) => (
        <SelectAddress
          value={value as AddressDto | null}
          addressTypes={addressTypes}
          canChangeAddressType={canChangeAddressType}
          patientId={addressSelectPatientId}
          contactId={addressSelectContactId}
          variant={addressSelectVariant}
          showSyncIcon={showAddressSyncIcon}
          disabled={isAddressDisabled}
          onAutoSelectFromUniqueMatch={onAutoSelectFromUniqueMatch}
          onContactSynced={applySyncedContact}
          onChange={(addressDto) => {
            onChange(addressDto);
            if (shouldClearContact(addressDto)) {
              setContactValue(null);
            }
            onAddressChange?.(addressDto);
          }}
        />
      )}
    />
  );

  const contactControl = (
    <Controller
      name={contactField}
      control={control}
      rules={contactRules}
      render={({ field: { onChange, value } }) => (
        <SelectAddressContact
          value={value as AddressContactDto | null}
          address={addressValue}
          patientId={contactSelectPatientId}
          variant={contactSelectVariant}
          showSyncIcon={showContactSyncIcon}
          useAllContactsList={useAllContactsList}
          createOptionLabel={createOptionLabel}
          disabled={isContactDisabled}
          onSynced={applySyncedContact}
          onChange={(contact, address) => {
            onChange(contact);
            if (contact && address && addressValue && !isSameAddress(addressValue, address)) {
              setValue(addressField, null as PathValue<TFieldValues, typeof addressField>, { shouldDirty: true });
            }
            onContactChange?.(contact, address);
          }}
        />
      )}
    />
  );

  const addressHelper = addressHelperText ? <FormHelperText>{addressHelperText}</FormHelperText> : null;
  const contactHelper = contactHelperText ? <FormHelperText>{contactHelperText}</FormHelperText> : null;

  if (layout === 'singleLine') {
    if (!addressFieldState || !contactFieldState) {
      throw new Error('InstitutionContactFields singleLine layout requires getFieldState');
    }

    return (
      <>
        <SingleLineFormControl label={addressLabel} fieldState={addressFieldState}>
          {addressControl}
          {addressHelper}
        </SingleLineFormControl>
        <SingleLineFormControl label={contactLabel} fieldState={contactFieldState}>
          {contactControl}
          {contactHelper}
        </SingleLineFormControl>
      </>
    );
  }

  const fields = (
    <>
      <FormControl
        {...addressFormControlProps}
        error={hasAddressError}
        sx={addressFormControlProps?.sx ?? (layout === 'row' ? { flex: 1 } : undefined)}>
        <FormLabel>{renderLabel(addressLabel)}</FormLabel>
        {addressControl}
        {addressHelper}
      </FormControl>
      <FormControl
        {...contactFormControlProps}
        error={hasContactError}
        sx={contactFormControlProps?.sx ?? (layout === 'row' ? { flex: 1 } : undefined)}>
        <FormLabel>{renderLabel(contactLabel)}</FormLabel>
        {contactControl}
        {contactHelper}
      </FormControl>
    </>
  );

  return layout === 'row' ? (
    <Stack direction={'row'} gap={2}>
      {fields}
    </Stack>
  ) : (
    fields
  );
}
